import GhHtmlElement from "@gudhub/gh-html-element";
import './components/ghAvatar/avatar.webcomponent.js';
import './components/createConversation.webcomponent.js';
import './components/usersSideBar/usersSideBar.js';
import './components/directMessageChat/directMessageChat.js';
import './components/activityFeed/activityFeed.js';
import './components/callProfile/callProfile.js';
import './components/emailInbox/emailInbox.js';
import './components/ghChat/ghChat.js';
import './components/calls/calls.js';
import html from "./send-message.html";
import { NavigationHistory } from './NavigationHistory.js';
import { initPushNotifications } from './pushNotifications/pushNotificationHandler.js';
import { getConnectedAppPhoneNumbers } from './connectedAppPhone.js';
import { getWorkspaceUsers } from './workspaceUsers.js';
import './style.scss';
import './panel.scss';

class GudhubMessenger extends GhHtmlElement {

    constructor() {
        super();
    }

    //********************* CONNECTED CALLBACK ********************************//
    // The global panel has no app-id/item-id/field-id attributes, so the base
    // class's attributeChangedCallback never calls onInit(). Call it directly
    // here instead, the same way GhAvatar does for its attribute-less mode.
    connectedCallback() {
        if(this._gudhubMessengerInitialized) return;
        this._gudhubMessengerInitialized = true;

        if(!this.getAttribute('app-id')) {
            // The element upgrades synchronously while main.js is still being
            // evaluated, before `window.gudhub` is assigned. Wait for it to
            // exist so onInit() (and waitForActiveUserId()) can safely read it.
            this.waitForGudhub().then(() => this.onInit()).catch((err) => {
                console.error('GudhubMessenger init failed', err);
            });
        }

        this.trackVisualViewport();
        this.trackHardwareBackButton();
    }

    //********************* HARDWARE BACK BUTTON ********************************//
    // Android only - iOS has no equivalent, which is why this had no counterpart
    // until now.
    //
    // By default Capacitor's back button closes the whole app. In a panel that
    // stacks (rail -> list -> conversation) that reads as a crash: the user taps
    // Back to leave a chat and lands on their home screen instead. Routing the
    // event through the panel's own history first makes the system gesture mean
    // what it means everywhere else on the platform.
    //
    // The plugin is read off window.Capacitor rather than imported, because
    // @capacitor/app is a dependency of the gudhub-mobile shell, not of this
    // web client - an import here would break the web build. Capacitor injects
    // its bridge into allow-listed remote origins, so the plugin is reachable
    // this way while the app runs inside the WebView, and simply absent in a
    // normal browser.
    trackHardwareBackButton() {
        const App = window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App;

        if(!App) return;

        const handle = App.addListener('backButton', ({ canGoBack }) => {
            if(this.handleBackButton()) return;

            // Nothing of ours left to close: fall back to normal browser
            // history, and only exit once even that is exhausted.
            if(canGoBack) window.history.back();
            else App.exitApp();
        });

        // The bridge object reached through window.Capacitor.Plugins returns
        // the listener handle synchronously, while the npm wrapper around the
        // same plugin returns a promise for it. This code deliberately uses
        // the bridge (see trackHardwareBackButton's comment on why), so it
        // must not assume either shape - assuming a promise threw a TypeError
        // on every launch and took the rest of connectedCallback with it.
        Promise.resolve(handle).then((resolved) => {
            this._backButtonHandle = resolved;
        });
    }

    // Returns true when the press was consumed, i.e. the caller must NOT let it
    // reach the system. Ordered deepest state first, so one press unwinds one
    // level rather than skipping straight out of the panel.
    handleBackButton() {
        if(!this.isMobile()) return false;

        // An open conversation goes back to the list it came from, through the
        // very same history stack the on-screen back arrow uses - so the two
        // can't drift apart.
        if(this.classList.contains('conversations--chat-active')) {
            this.navigateBack();
            return true;
        }

        // On the list itself: close the messenger and reveal the app under it.
        // Same outcome as the X in the top panel.
        if(this.classList.contains('conversations--expanded')) {
            this.setExpanded(false);
            return true;
        }

        return false;
    }

    //********************* VISUAL VIEWPORT ********************************//
    // Keeps the panel matched to the area the keyboard leaves behind.
    //
    // The panel is position:fixed, and on iOS a fixed element is laid out
    // against the LAYOUT viewport, which the keyboard does not shrink - iOS
    // scrolls the page under the keyboard instead. So height:100% keeps
    // resolving to the full screen and the composer stays hidden behind the
    // keyboard, which is exactly the "the input won't come up" symptom.
    //
    // window.visualViewport reports the part actually on screen, and it is
    // authoritative regardless of which resize mode @capacitor/keyboard is set
    // to - if the plugin already shrank the WebView, visual and layout
    // viewports agree and these values are simply the full height.
    trackVisualViewport() {
        const viewport = window.visualViewport;

        // No VisualViewport (older desktop browsers): the CSS falls back to
        // 100%/0px, i.e. exactly the previous behaviour.
        if(!viewport) return;

        const root = document.documentElement;

        const apply = () => {
            // A slice of the layout viewport missing from the visual one means
            // the keyboard (or another OS panel) is covering it. 120px rather
            // than a few pixels so browser chrome collapsing on scroll - or a
            // scrollbar's own thickness - doesn't read as a keyboard.
            const covered = window.innerHeight - viewport.height - viewport.offsetTop;
            const keyboardOpen = covered > 120;

            this.classList.toggle('conversations--keyboard-open', keyboardOpen);

            if(!keyboardOpen) {
                // Hand the panel back to plain height:100%/top:0 rather than
                // pinning it to a pixel value. visualViewport.height excludes
                // scrollbars and carries sub-pixel rounding, so publishing it
                // while nothing is covering the screen leaves the panel a
                // hair short of the viewport - enough for the sidebar to
                // overflow and grow scrollbars it never had before.
                root.style.removeProperty('--gh-viewport-height');
                root.style.removeProperty('--gh-viewport-top');
                return;
            }

            // offsetTop is how far iOS has scrolled the visual viewport away
            // from the layout viewport. Feeding it back into `top` pins the
            // panel to what the user can actually see.
            root.style.setProperty('--gh-viewport-height', `${viewport.height}px`);
            root.style.setProperty('--gh-viewport-top', `${viewport.offsetTop}px`);
        };

        this._onViewportChange = apply;
        viewport.addEventListener('resize', apply);
        viewport.addEventListener('scroll', apply);
        apply();
    }

    disconnectedCallback() {
        if(super.disconnectedCallback) super.disconnectedCallback();

        const viewport = window.visualViewport;

        if(viewport && this._onViewportChange) {
            viewport.removeEventListener('resize', this._onViewportChange);
            viewport.removeEventListener('scroll', this._onViewportChange);
            this._onViewportChange = null;
        }

        if(this._backButtonHandle) {
            this._backButtonHandle.remove();
            this._backButtonHandle = null;
        }
    }

    //********************* WAIT FOR GUDHUB ********************************//
    waitForGudhub() {
        if(window.gudhub) return Promise.resolve();

        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if(window.gudhub) {
                    clearInterval(interval);
                    resolve();
                }
            }, 50);
        });
    }

    async onInit() {
        this.appId = this.scope?.appId || '';
        this.itemId = this.scope?.itemId || '';
        this.fieldId = this.scope?.fieldId || '';
        this.app_id = this.appId;
        this.item_id = this.itemId;
        this.field_id = this.fieldId;

        this.activeUserId = await this.waitForActiveUserId();

        // Warms gudhub.getUserById()'s cache for every workspace user in one
        // request, before gh-users-side-bar/gh-activity-feed/gh-direct-message-chat
        // etc. mount below and start resolving DM partners/group members/senders
        // one getUserById() call at a time. Failure here just means those calls
        // fall back to their normal per-id fetch, so it can't block init.
        await getWorkspaceUsers().catch(err => console.error('getUserWorkspace prefetch failed', err));

        this.navigationHistory = new NavigationHistory();
        this.itemContext = null; // { app_id, view_id, item_id } | null
        this.itemPhoneNumbers = []; // connected-app phone values for itemContext: [{fieldId, fieldName, value}]
        this._autoDialedNumber = null; // last itemPhoneNumbers[0].value actually pushed into gh-call-profile - lets refreshPhoneTabForItem tell a live item-driven number apart from one the user has since edited by hand
        this.$rootScope = gudhub.ghconstructor.angularInjector.get('$rootScope');
        this.$routeParams = gudhub.ghconstructor.angularInjector.get('$routeParams');

        this.updateItemContext();
        this.$rootScope.$on('$routeChangeSuccess', () => {
            this.updateItemContext();
        });

        this.model = (this.app_id && this.field_id) ? await gudhub.getField(this.app_id, this.field_id) : null;

        this.hasMessengers = !!(this.model && this.model.data_model.messengers && this.model.data_model.messengers.length);

        this.messengers = {};
        this.toast = gudhub.ghconstructor.angularInjector.get('ghToastService');

        // Mobile bottom-nav tab (see showAppsTab/showChatTab/showActivityTab) - 'apps' is
        // the default so the panel starts collapsed and the underlying route is visible.
        this.mobileActiveTab = 'apps';

        // The DM partner of whichever direct_message conversation is currently open
        // in gh-direct-message-chat, or null if none is - drives whether the Phone
        // tab (showPhoneTab) opens straight to that contact's call profile or to the
        // contact picker. Kept in sync by the switch_chat_view handler and showAppsTab.
        this.activeDmUser = null;

        if(this.hasMessengers) {
            const slackMessenger = this.model.data_model.messengers.find(messenger => messenger.messenger_name === 'slack');
            if(slackMessenger && !slackMessenger.messenger_settings.page_id) {
                const token = await gudhub.getToken();
                const res = await fetch(`${gudhub.config.node_server_url}/integrations?token=${encodeURIComponent(token)}`);
                const integrations = await res.json();
                this.slackIntegration = integrations.data.find(integration => integration.service_id === 'slack' && integration.field_id === this.fieldId);
                slackMessenger.messenger_settings.page_id = this.slackIntegration.service_user_id;
                gudhub.updateField(this.appId, this.model);
            }

            for(const index of Object.keys(this.model.data_model.messengers)) {
                const messenger = this.model.data_model.messengers[index];

                this.messengers[index] = {
                    token: messenger.messenger_settings.bot_token,
                    messenger_user_id: await this.getMessengerUserId(messenger),
                    photo_field_id: this.model.data_model.messengers[index].messenger_settings.photo_field,
                    messenger: messenger.messenger_name,
                }

                if(messenger.messenger_settings.use_threads) {
                    this.messengers[index].message_id_for_threads = await gudhub.getFieldValue(this.appId, this.itemId, messenger.messenger_settings.thread_field_id);
                }
            }
        }

        super.render(html);

        if(this.hasMessengers) {
            // Get conversation from another component with dispatch event and check if conversation exist for show/hide create channel button in slack
            this.addEventListener("chat_init", async function(event) {
                const { conversation } = event.detail;

                const selectedOption = this.querySelector('.messenger-select');

                let messenger_id = true, thread_id = false;

                for(const index in this.messengers) {
                    messenger_id = this.messengers[index].messenger_user_id;
                    thread_id = await gudhub.getFieldValue(this.app_id, this.item_id, this.model.data_model.messengers[index].messenger_settings.thread_field_id)
                }

                selectedOption.addEventListener('change', async (event) => {

                    const createGroupBtn = this.querySelector('slack-create-conversation');
                    const sendBtn = this.querySelector('.send_button');

                    if(event.target.value === 'slack' && !conversation.messages.length && !messenger_id) {
                        createGroupBtn.style.display = 'block';
                        sendBtn.style.display = 'none';
                    } else {
                        sendBtn.style.display = 'block';
                        createGroupBtn.style.display = 'none';
                    }
                });

                const options = this.querySelectorAll(`.messenger-select option`);
                for(let i = 0; i < options.length; i++) {
                    const option = options[i];
                    if(option.dataset.id == conversation.messages[conversation.messages.length - 1]?.page_id) {
                        option.setAttribute('selected', '');

                        if(option.value == 'slack' && !conversation.messages.length && !messenger_id) {
                            const createGroupBtn = this.querySelector('slack-create-conversation');
                            const sendBtn = this.querySelector('.send_button');
                            createGroupBtn.style.display = 'block';
                            sendBtn.style.display = 'none';
                        }
                    }

                    if(options.length === 1 && option.value === 'slack' && !messenger_id && !this.model.data_model.messengers[0].messenger_settings.use_threads) {
                        const createGroupBtn = this.querySelector('slack-create-conversation');
                        const sendBtn = this.querySelector('.send_button');
                        createGroupBtn.style.display = 'block';
                        sendBtn.style.display = 'none';
                    }

                    if(this.model.data_model.messengers[0].messenger_settings.use_threads && !thread_id) {
                        const sendBtn = this.querySelector('.send_button');
                        const createThreadBtn = this.querySelector('.create_thread');
                        sendBtn.style.display = 'none';
                        createThreadBtn.style.display = 'block';
                    }
                }
            });
        }

        //********************* SWITCH CHAT VIEW ********************************//
        // Toggles .multi-chat between the existing "Messengers" view, the GudHub
        // Messenger DM chat, and (when no external messengers are configured)
        // the placeholder panel.
        // * event (CustomEvent) — detail: { view: 'messengers'|'direct_message'|'item_discussion'|'channel', user_id?: string, messenger_index?: number, conversation_id?: string, item_ref?: Object, name?: string }
        this.addEventListener('switch_chat_view', async (event) => {
            const { view, user_id, messenger_index, conversation_id, item_ref, name } = event.detail;
            this.setMobileTab(['activity', 'email'].includes(view) ? view : 'chat');
            const messengersView = this.querySelector('.messengers_view');
            const dmChat = this.querySelector('gh-direct-message-chat');
            const activityFeed = this.querySelector('gh-activity-feed');
            const callProfile = this.querySelector('gh-call-profile');
            const emailInbox = this.querySelector('gh-email-inbox');

            if(view === 'activity') {
                this.navigationHistory.push({ view: 'sidebar' });
                this.showMobileChat();

                this.activeDmUser = null;
                messengersView.classList.add('hidden');
                dmChat.classList.add('hidden');
                dmChat.closeConversation();
                callProfile.classList.add('hidden');
                emailInbox.classList.add('hidden');
                activityFeed.classList.remove('hidden');
                activityFeed.refresh();
            } else if(view === 'email') {
                this.navigationHistory.push({ view: 'sidebar' });
                this.showMobileChat();

                this.activeDmUser = null;
                messengersView.classList.add('hidden');
                dmChat.classList.add('hidden');
                dmChat.closeConversation();
                callProfile.classList.add('hidden');
                activityFeed.classList.add('hidden');
                emailInbox.classList.remove('hidden');
                emailInbox.refresh();
            } else if(view === 'direct_message' || view === 'item_discussion' || view === 'channel' || view === 'no_discussion') {
                this.navigationHistory.push({ view: 'sidebar' });
                this.showMobileChat();

                messengersView.classList.add('hidden');
                activityFeed.classList.add('hidden');
                callProfile.classList.add('hidden');
                emailInbox.classList.add('hidden');
                dmChat.classList.remove('hidden');

                this.querySelector('gh-users-side-bar')?.focusConversation(event.detail);

                if(view === 'direct_message') {
                    await dmChat.openConversation(user_id, this.itemContext);
                    this.activeDmUser = dmChat.dmState?.otherUser
                        ? { ...dmChat.dmState.otherUser, user_id: dmChat.dmState.otherUserId }
                        : null;
                } else if(view === 'no_discussion') {
                    this.activeDmUser = null;
                    await dmChat.showNoDiscussion(item_ref);
                } else {
                    this.activeDmUser = null;
                    if(view === 'channel') {
                        await dmChat.openChannel(conversation_id, name);
                    } else {
                        await dmChat.openItemDiscussion(conversation_id, item_ref);
                    }
                }
            } else {
                this.activeDmUser = null;
                dmChat.classList.add('hidden');
                dmChat.closeConversation();
                activityFeed.classList.add('hidden');
                callProfile.classList.add('hidden');
                emailInbox.classList.add('hidden');
                if(this.hasMessengers) {
                    messengersView.classList.remove('hidden');

                    if(messenger_index !== undefined) {
                        const messengerSelect = this.querySelector('.messenger-select');
                        messengerSelect.selectedIndex = messenger_index;
                        messengerSelect.dispatchEvent(new Event('change'));
                    }
                }
            }
        });

        //********************* CONVERSATION READ ********************************//
        // Forwards gh-direct-message-chat's "marked as read" notification to the
        // sidebar, so it can clear the corresponding unread badge (DM, channel,
        // and/or item-thread - whichever conversation was just marked as read).
        // * event (CustomEvent) — detail: { conversation_id: string, user_id: string|null }
        this.addEventListener('conversation_read', (event) => {
            const { conversation_id, user_id } = event.detail;
            const sidebar = this.querySelector('gh-users-side-bar');

            if(user_id) sidebar?.markConversationRead(user_id);
            sidebar?.markItemConversationRead(conversation_id);
            sidebar?.markChannelRead(conversation_id);
        });

        //********************* DIRECT CONVERSATION CREATED ********************************//
        // Forwards gh-direct-message-chat's notification that a DM was opened with
        // a user not yet in the sidebar's "Direct Messages" list, so the sidebar
        // can add them without a reload.
        // * event (CustomEvent) — detail: { user_id: string }
        this.addEventListener('direct_conversation_created', (event) => {
            this.querySelector('gh-users-side-bar')?.addDirectConversation(event.detail.user_id);
        });

        //********************* ITEM THREAD DISCUSSED ********************************//
        // Forwards gh-direct-message-chat's "discuss this item"/auto-join
        // notifications to the sidebar, so the flat "Item Discussions" list and
        // any DM/channel row(s) matching this item-thread's participants pick up
        // the newly created/joined item-thread immediately (notifyParticipants
        // excludes the actor, so without this they'd only see it after a reload).
        // * event (CustomEvent) — detail: { conversation_id, item_ref, unread_count, participants }
        this.addEventListener('item_thread_discussed', (event) => {
            this.querySelector('gh-users-side-bar')?.addItemDiscussion(event.detail);
        });

        //********************* OPEN CALL PROFILE ********************************//
        // Forwards the DM header's call-button click to the Phone tab - calls are
        // only ever placed from there now (see showPhoneTab).
        this.addEventListener('open_call_profile', () => {
            this.showPhoneTab();
        });

        //********************* START CALL ********************************//
        // Forwards gh-call-profile's phone/camera button click to the global
        // <gh-calls> element, which owns the persistent SIP.js registration.
        // * event (CustomEvent) — detail: { callee_id: string, caller_id: string, video: boolean }
        this.addEventListener('start_call', (event) => {
            // <gh-calls> is a page-level singleton mounted directly in
            // index_template.ejs (a sibling of <gudhub-messenger>, not a
            // descendant of it) - this must stay a document-wide lookup,
            // not scoped to `this` (the messenger element).
            const ghCalls = document.querySelector('gh-calls');

            ghCalls?.startOutgoingCall(event.detail);
        });

        initPushNotifications(this);
    }

    //********************* UPDATE ITEM CONTEXT ********************************//
    // Derives the current open_item context from $routeParams (or null), and
    // pushes it to gh-direct-message-chat and gh-users-side-bar if it changed.
    updateItemContext() {
        const { actionType, param_1, param_2, param_3 } = this.$routeParams;

        const newContext = (actionType === 'open_item' && param_1 && param_3)
            ? { app_id: String(param_1), view_id: param_2 ? String(param_2) : null, item_id: String(param_3) }
            : null;

        const a = newContext, b = this.itemContext;
        const changed = (!a !== !b) || (a && b && (a.app_id !== b.app_id || a.item_id !== b.item_id || a.view_id !== b.view_id));

        this.itemContext = newContext;

        if(changed) {
            this.querySelector('gh-direct-message-chat')?.setItemContext(newContext);
            this.querySelector('gh-users-side-bar')?.setItemContext(newContext);

            this.itemPhoneNumbers = [];
            this.refreshPhoneTabForItem();

            if(newContext) {
                getConnectedAppPhoneNumbers(newContext.app_id, newContext.item_id).then((numbers) => {
                    // Only apply it if the user hasn't already navigated elsewhere
                    // while this lookup was in flight.
                    if(this.itemContext === newContext) {
                        this.itemPhoneNumbers = numbers;
                        this.refreshPhoneTabForItem();
                    }
                });
            }
        }
    }

    //********************* REFRESH PHONE TAB FOR ITEM ********************************//
    // Keeps an already-open Phone tab's keypad in sync as the user navigates
    // between items - without this, newly resolved itemPhoneNumbers only ever
    // reached gh-call-profile the *next* time showPhoneTab() was explicitly
    // invoked, so switching items while the tab was already open left it
    // showing the previous item's numbers (or none). Only acts when the tab
    // is actually visible and no explicit contact/DM is being shown (mirrors
    // showPhoneTab's own priority order) - and never overwrites a number the
    // user has since typed/edited by hand on the keypad.
    refreshPhoneTabForItem() {
        if(this.mobileActiveTab !== 'phone' || this.activeDmUser) return;
        if(!this.classList.contains('conversations--expanded')) return;

        const callProfile = this.querySelector('gh-call-profile');
        if(!callProfile) return;

        const userEdited = callProfile.viewMode === 'keypad' && callProfile.dialedNumber !== this._autoDialedNumber;
        if(userEdited) return;

        if(this.itemPhoneNumbers.length) {
            callProfile.showItemPhoneNumbers(this.itemPhoneNumbers);
            this._autoDialedNumber = this.itemPhoneNumbers[0].value;
        } else if(callProfile.viewMode !== 'profile') {
            callProfile.showPicker();
            this._autoDialedNumber = null;
        }
    }

    //********************* WAIT FOR ACTIVE USER ID ********************************//
    // gudhub.storage.user.user_id is filled in asynchronously after auth resolves,
    // and may not be set yet at the time this global panel connects.
    async waitForActiveUserId() {
        if(gudhub.storage.user?.user_id) return gudhub.storage.user.user_id;

        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if(gudhub.storage.user?.user_id) {
                    clearInterval(interval);
                    resolve(gudhub.storage.user.user_id);
                }
            }, 100);

            setTimeout(() => {
                clearInterval(interval);
                resolve(gudhub.storage.user?.user_id);
            }, 10000);
        });
    }

    //********************* SET EXPANDED STATE ********************************//
    // Keeps <body> in sync with the panel's expanded/collapsed state so layout
    // CSS (shifting gh-window forms) can react to it.
    // * expanded (boolean) — true to expand the panel, false to collapse
    setExpanded(expanded) {
        this.classList.toggle('conversations--expanded', expanded);

        const injector = angular.element(document.querySelector('body')).injector();
        const $rootScope = injector.get('$rootScope');
        $rootScope.messengerExpanded = expanded;
        $rootScope.$apply();

        if(!expanded) {
            this.classList.remove('conversations--chat-active');
            this.navigationHistory?.clear();
        }
    }

    //********************* TOGGLE CONVERSATIONS PANEL ********************************//
    // Expands/collapses the panel between the icon rail and the full sidebar+chat view.
    toggleConversationsPanel() {
        this.setExpanded(!this.classList.contains('conversations--expanded'));
    }

    isMobile() {
        return window.matchMedia('(max-width: 767px)').matches;
    }

    showMobileChat() {
        if(this.isMobile()) {
            this.classList.add('conversations--chat-active');
        }
    }

    showMobileSidebar() {
        if(this.isMobile()) {
            this.classList.remove('conversations--chat-active');
        }
    }

    //********************* SHOW APPS TAB ********************************//
    // Mobile bottom-nav "Apps" button - collapses the messenger panel so the
    // underlying app route is visible again, and navigates to /apps (matches
    // Slack's "Home" tab: always takes you back to the app's own content).
    showAppsTab() {
        this.setMobileTab('apps');
        this.setExpanded(false);
        this.activeDmUser = null;
        this.querySelector('gh-call-profile')?.classList.add('hidden');

        const $location = gudhub.ghconstructor.angularInjector.get('$location');
        $location.path('/apps');
        this.$rootScope.$apply();
    }

    //********************* SHOW CHAT TAB ********************************//
    // Mobile bottom-nav "Chat" button - tapping it again while it's already
    // the active tab collapses the panel (see hideMobilePanel). Otherwise it
    // expands the panel and normally always shows the sidebar list - even if
    // Activity was showing in .multi-chat a moment ago - matching Slack's
    // "DMs" tab always landing on the list, not a stale previous view.
    //
    // The one exception is an item page: the desktop panel already reacts to
    // itemContext this same way on every route change (gh-users-side-bar's
    // applyItemDiscussionState), auto-switching to the item's own discussion
    // thread if one exists, or its "no discussion yet" composer if not - so
    // mobile lands on the same view directly instead of making the user tap
    // through the list to find/start it.
    async showChatTab() {
        if(this.mobileActiveTab === 'chat' && this.classList.contains('conversations--expanded')) {
            this.hideMobilePanel();
            return;
        }

        this.setMobileTab('chat');
        this.setExpanded(true);

        if(this.itemContext) {
            await this.querySelector('gh-users-side-bar')?.applyItemDiscussionState(this.itemContext, { respectOpenConversation: false });
        } else {
            this.showMobileSidebar();
        }
    }

    //********************* SHOW ACTIVITY TAB ********************************//
    // Mobile bottom-nav "Activity" button - same toggle-off behavior as
    // showChatTab. Otherwise delegates to gh-users-side-bar's existing
    // selectActivity(), which expands the panel and opens the feed.
    showActivityTab() {
        if(this.mobileActiveTab === 'activity' && this.classList.contains('conversations--expanded')) {
            this.hideMobilePanel();
            return;
        }

        this.setMobileTab('activity');
        this.querySelector('gh-users-side-bar')?.selectActivity();
    }

    //********************* SHOW EMAIL TAB ********************************//
    // Mobile bottom-nav "Email" button - same toggle-off behavior as
    // showChatTab/showActivityTab. Otherwise delegates to gh-users-side-bar's
    // existing selectEmail(), which expands the panel and opens the inbox.
    showEmailTab() {
        if(this.mobileActiveTab === 'email' && this.classList.contains('conversations--expanded')) {
            this.hideMobilePanel();
            return;
        }

        this.setMobileTab('email');
        this.querySelector('gh-users-side-bar')?.selectEmail();
    }

    //********************* SHOW PHONE TAB ********************************//
    // Mobile bottom-nav "Phone" button - same toggle-off behavior as
    // showChatTab/showActivityTab. Also reached from the DM header's call
    // button via the open_call_profile event, and from a sidebar row's
    // hover phone icon (gh-users-side-bar.openPhoneForUser), which passes an
    // explicit user to jump straight to their profile regardless of which DM
    // is active - so the toggle-off only applies to the no-argument callers.
    // * user (Object, optional) - user to show; falls back to activeDmUser,
    //   then the current open item's connected-app phone numbers (see
    //   updateItemContext), then the contact picker.
    showPhoneTab(user) {
        if(!user && this.mobileActiveTab === 'phone' && this.classList.contains('conversations--expanded')) {
            this.hideMobilePanel();
            return;
        }

        this.setMobileTab('phone');
        this.setExpanded(true);
        this.showMobileChat();

        this.querySelector('.messengers_view').classList.add('hidden');
        this.querySelector('gh-direct-message-chat').classList.add('hidden');
        this.querySelector('gh-activity-feed').classList.add('hidden');
        this.querySelector('gh-email-inbox').classList.add('hidden');
        const callProfile = this.querySelector('gh-call-profile');
        callProfile.classList.remove('hidden');

        user = user || this.activeDmUser;
        if(user) {
            callProfile.showUser(user);
        } else if(this.itemPhoneNumbers.length) {
            callProfile.showItemPhoneNumbers(this.itemPhoneNumbers);
            this._autoDialedNumber = this.itemPhoneNumbers[0].value;
        } else {
            callProfile.showPicker();
            this._autoDialedNumber = null;
        }
    }

    //********************* HIDE MOBILE PANEL ********************************//
    // Collapses the panel back down without navigating anywhere (unlike
    // showAppsTab) - used when re-tapping the already-active Chat/Activity/Phone/Email
    // tab to dismiss it.
    hideMobilePanel() {
        this.setMobileTab('apps');
        this.setExpanded(false);
    }

    //********************* SET MOBILE TAB ********************************//
    // Updates the active-tab highlight on the mobile bottom nav without a
    // full re-render. Called both from the tab buttons themselves and from
    // switch_chat_view, so the highlight stays correct however the view changed.
    // * tab (string) — 'apps' | 'chat' | 'activity' | 'phone' | 'email'
    setMobileTab(tab) {
        this.mobileActiveTab = tab;
        this.querySelectorAll('.gudhub_mobile_nav__item').forEach(button => {
            button.classList.toggle('gudhub_mobile_nav__item--active', button.dataset.tab === tab);
        });
    }

    navigateBack() {
        const entry = this.navigationHistory.pop();

        if(!entry || entry.view === 'sidebar') {
            this.showMobileSidebar();

            const dmChat = this.querySelector('gh-direct-message-chat');
            dmChat?.closeConversation();
            dmChat?.classList.add('hidden');

            const activityFeed = this.querySelector('gh-activity-feed');
            activityFeed?.classList.remove('hidden');
            activityFeed?.refresh();
            return;
        }

        this.dispatchEvent(new CustomEvent('switch_chat_view', {
            bubbles: false,
            detail: entry
        }));
    }

    getFileType(file) {
        const fileExtension = file.name.split('.').pop();

        if(['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) {
            return 'image';
        }

        if(['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv'].includes(fileExtension)) {
            return 'video';
        }

        if(['mp3', 'wav', 'ogg', 'flac', 'aac', 'webm'].includes(fileExtension)) {
            return 'audio';
        }

        return 'file';
    }

    async sendMessage(element) {
        event.preventDefault();

        const loader = this.querySelector('.send_message_loader');
        const textarea = element.querySelector('textarea');
        const button = element.querySelector('button');
        const text = textarea.value;
        const messengerSelect = this.querySelector('.messenger-select');
        const messenger = messengerSelect.options[messengerSelect.selectedIndex].value;

        const uploadInput = this.querySelector('.upload input');

        if(!text.length && !uploadInput.files.length) {
            return;
        }

        loader.style.display = 'block';
        loader.style.right = '75px';
        button.setAttribute('disabled', '');

        for(const index of Object.keys(this.model.data_model.messengers)) {
            const messenger = this.model.data_model.messengers[index];

            this.messengers[index].messenger_user_id = await this.getMessengerUserId(messenger);

            if(messenger.messenger_settings.use_threads) {
                this.messengers[index].message_id_for_threads = await gudhub.getFieldValue(this.appId, this.itemId, messenger.messenger_settings.thread_field_id);
            }
        }

        if(uploadInput.files.length > 0) {

            const file = uploadInput.files[0];
            const fileType = this.getFileType(file);

            const gudhubFile = await this.uploadFileToGudHub(file);

            let response = await fetch(`${gudhub.config.node_server_url}/conversation/send-attachment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    messenger,
                    messenger_user_id: this.messengers[messengerSelect.selectedIndex].messenger_user_id,
                    token: this.messengers[messengerSelect.selectedIndex].token,
                    app_id: this.app_id,
                    field_id: this.field_id,
                    item_id: this.item_id,
                    user_id: this.activeUserId,
                    message_id: this.messengers[messengerSelect.selectedIndex].message_id_for_threads,
                    page_id: this.model.data_model.messengers[messengerSelect.selectedIndex].messenger_settings.page_id,
                    attachment: {
                        url: gudhubFile.url,
                        type: fileType
                    },
                    user: gudhub.storage.user
                })
            });

            response = await response.json();

            if(!response.content && response.status != 200) {
                this.showErrorMessage(response.message);
            }

            loader.style.display = 'none';
            button.removeAttribute('disabled');

            uploadInput.value = '';

        } else {
             let response = await fetch(`${gudhub.config.node_server_url}/conversation/send-message`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    messenger,
                    messenger_user_id: this.messengers[messengerSelect.selectedIndex].messenger_user_id,
                    token: this.messengers[messengerSelect.selectedIndex].token,
                    app_id: this.app_id,
                    field_id: this.field_id,
                    item_id: this.item_id,
                    user_id: this.activeUserId,
                    message_id: this.messengers[messengerSelect.selectedIndex].message_id_for_threads,
                    page_id: this.model.data_model.messengers[messengerSelect.selectedIndex].messenger_settings.page_id,
                    text,
                    user: gudhub.storage.user
                })
            });

            response = await response.json();

            if(!response.content && response.status != 200) {
                this.showErrorMessage(response.message);
            }

            loader.style.display = 'none';
            button.removeAttribute('disabled');
            textarea.value = '';
        }

        this.value = 0;

    }

    showErrorMessage(message) {

        const template = `<md-toast>
                <p>${message}</p>
                <div class="gh-btn" gh-icon="cross 0893d2 30px normal" ng-click="closeToast()"></div>
            </md-toast>`;

            const options = {
                template,
                hideDelay: 10000,
                scope: this.scope.$new(true),
            };

            this.toast.customToast(options)
    }

    async createThread() {

        const messengerSelect = this.querySelector('.messenger-select');
        const messenger = messengerSelect.options[messengerSelect.selectedIndex].value;

        const loader = this.querySelector('.send_message_loader');

        loader.style.right = '125px';

        loader.style.display = 'block';

        let response = await fetch(`${gudhub.config.node_server_url}/conversation/thread/create/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                messenger,
                messenger_user_id: this.messengers[messengerSelect.selectedIndex].messenger_user_id,
                app_id: this.app_id,
                field_id: this.field_id,
                item_id: this.item_id,
                user_id: this.activeUserId,
                thread_field_id: this.model.data_model.messengers[messengerSelect.selectedIndex].messenger_settings.thread_field_id,
                text: `<${window.location.href}|${await gudhub.getFieldValue(this.appId, this.itemId, this.model.data_model.messengers[messengerSelect.selectedIndex].messenger_settings.user_name_field_id)}>`,
                user: gudhub.storage.user
            })
        });

        response = await response.json();

        if(!response.ok) {
            this.showErrorMessage(response.message);
            loader.style.display = 'none';
            return;
        }

        loader.style.display = 'none';

        const sendBtn = this.querySelector('.send_button');
        const createThreadBtn = this.querySelector('.create_thread');
        sendBtn.style.display = 'block';
        createThreadBtn.style.display = 'none';
    }

    async uploadFileToGudHub(file) {

        const base64 = await this.toBase64(file);

        const response = await gudhub.uploadFileFromString({
            format: 'base64',
            source: base64.substring(base64.indexOf(',') + 1),
            file_name: file.name,
            extension: file.name.split('.').pop(),
            app_id: this.app_id,
            item_id: this.item_id,
            element_id: this.field_id
        });

        return response;

    }

    async getMessengerUserId(messenger) {
        // If user_id_field is not configured, fall back to item_id as the conversation key
        return messenger.messenger_settings.user_id_field
            ? await gudhub.getFieldValue(this.app_id, this.item_id, messenger.messenger_settings.user_id_field)
            : this.item_id;
    }

    toBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = function () {
                resolve(reader.result);
            };
            reader.onerror = function (error) {
                reject(error);
            };
        });
    }

}

if(!customElements.get('gudhub-messenger')){
    customElements.define('gudhub-messenger', GudhubMessenger);
}
